import { useEffect, useCallback } from 'react';
import { useGameStore, INPUT } from '@hollowveil/shared';

export function useInputHandler() {
  const { updateInput, setInputMode, switchWorld, togglePause, currentRoom } =
    useGameStore();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Detect keyboard usage
      setInputMode('keyboard');

      const key = e.code;
      const keys = INPUT.keyboard;

      // Movement
      if (keys.up.includes(key)) {
        updateInput({ movement: { x: 0, y: 1 } });
      } else if (keys.down.includes(key)) {
        updateInput({ movement: { x: 0, y: -1 } });
      } else if (keys.left.includes(key)) {
        updateInput({ movement: { x: -1, y: 0 } });
      } else if (keys.right.includes(key)) {
        updateInput({ movement: { x: 1, y: 0 } });
      }

      // Interact
      if (keys.interact.includes(key)) {
        updateInput({ interact: true });
      }

      // Switch world
      if (keys.switchWorld.includes(key) && currentRoom?.hasSpectralLayer) {
        switchWorld();
      }

      // Pause
      if (keys.pause.includes(key)) {
        togglePause();
      }
    },
    [updateInput, setInputMode, switchWorld, togglePause, currentRoom]
  );

  const handleKeyUp = useCallback(
    (e: KeyboardEvent) => {
      const key = e.code;
      const keys = INPUT.keyboard;

      // Reset movement
      if (
        keys.up.includes(key) ||
        keys.down.includes(key) ||
        keys.left.includes(key) ||
        keys.right.includes(key)
      ) {
        updateInput({ movement: { x: 0, y: 0 } });
      }

      // Reset interact
      if (keys.interact.includes(key)) {
        updateInput({ interact: false });
      }
    },
    [updateInput]
  );

  // Detect touch device
  const handleTouchStart = useCallback(() => {
    setInputMode('touch');
  }, [setInputMode]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('touchstart', handleTouchStart, { once: true });

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp, handleTouchStart]);
}
